import { frontendDocumentType, type PrintResult, type PrintScope } from './printRules'

export type AutoPrintConfig = {
  documentType: string
  documentName: string
  printerName?: string
  deviceId?: string
  previewEnabled: boolean
  printCopies: number
  autoPrintOnNewCharge?: boolean
  autoPrintOnChargeCompleted?: boolean
  sort?: number
}

export type PrintMessage = {
  type: string
  uid: string
  businessId: string | number
  scope: PrintScope
  trigger: 'onNew' | 'onPaid'
  autoPrintConfigs: AutoPrintConfig[]
}

// 使用消息中的打印配置，执行前仍需校验当前事件对应的自动打印开关。
export function parsePrintMessages(raw: unknown): PrintMessage[] {
  try {
    const envelope = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!envelope || typeof envelope !== 'object') return []
    const type = String(envelope.type)
    if (type !== '2000' && type !== '3000' && type !== '5003') return []
    const content = typeof envelope.content === 'string' ? JSON.parse(envelope.content) : envelope.content
    if (!content || typeof content.uid !== 'string' || !content.uid.trim()) return []
    const id = content.businessId
    if (!((typeof id === 'number' && Number.isSafeInteger(id) && id > 0) || (typeof id === 'string' && /^[1-9]\d*$/.test(id)))) return []
    const autoPrintConfigs = Array.isArray(content.autoPrintConfigs)
      ? content.autoPrintConfigs
        .filter((item: any) => item && typeof item.documentType === 'string')
        .map((item: any) => ({ ...item, documentType: frontendDocumentType(item.documentType) }))
        .filter((item: any) => item.documentType)
      : []
    if (type === '2000' && content.broadcast !== true) return []
    if (type === '2000') {
      return [{ type, uid: content.uid, businessId: id, scope: 'charge', trigger: 'onNew', autoPrintConfigs }]
    }
    if (type === '5003') {
      return [{ type, uid: content.uid, businessId: id, scope: 'pharmacy', trigger: 'onNew', autoPrintConfigs }]
    }
    return [{ type, uid: content.uid, businessId: id, scope: 'charge', trigger: 'onPaid', autoPrintConfigs }]
  } catch {
    return []
  }
}

type PrintHistory = { key: string; status: string; at: number }[]
type QueueOptions = {
  readHistory: () => PrintHistory
  writeHistory: (rows: PrintHistory) => void
  execute: (message: PrintMessage, config: AutoPrintConfig) => Promise<PrintResult | undefined>
  isActive: () => boolean
  reportError: (config: AutoPrintConfig | null, error: unknown) => void
}

export function createPrintMessageQueue({ readHistory, writeHistory, execute, isActive, reportError }: QueueOptions) {
  let queue = Promise.resolve()
  return (message: PrintMessage) => {
    const result = queue.then(async () => {
      if (!isActive()) return
      // 药房 5003 的「有新发药单」也使用 autoPrintOnChargeCompleted 字段。
      const enabledField = message.scope === 'charge' && message.trigger === 'onNew'
        ? 'autoPrintOnNewCharge'
        : 'autoPrintOnChargeCompleted'
      const configs = message.autoPrintConfigs.filter(config =>
        config[enabledField] === true && config.printerName)
      for (const config of configs) {
        if (!isActive()) return
        const key = JSON.stringify([message.type, message.uid, config.documentType])
        const history = readHistory()
        if (history.some(item => item.key === key)) continue
        const entry = { key, status: 'pending', at: Date.now() }
        history.push(entry)
        if (history.length > 1000) history.splice(0, history.length - 1000)
        writeHistory(history)
        try {
          const printed = await execute(message, config)
          entry.status = printed?.status || 'skipped'
        } catch (error) {
          entry.status = 'failed'
          reportError(config, error)
        }
        writeHistory(history)
      }
    })
    queue = result.catch(error => reportError(null, error))
    return queue
  }
}
